import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Message } from '../../../store/message';
import Button from '../Button';

interface ConfirmProps {
  message: Message;
  onClose: () => void;
}

const variants = {
  info: {
    icon: Info,
    className: 'bg-white dark:bg-gray-800',
    iconClass: 'text-blue-500 dark:text-blue-400',
  },
  success: {
    icon: CheckCircle,
    className: 'bg-white dark:bg-gray-800',
    iconClass: 'text-green-500 dark:text-green-400',
  },
  warning: {
    icon: AlertTriangle,
    className: 'bg-white dark:bg-gray-800',
    iconClass: 'text-yellow-500 dark:text-yellow-400',
  },
  error: {
    icon: AlertCircle,
    className: 'bg-white dark:bg-gray-800',
    iconClass: 'text-red-500 dark:text-red-400',
  },
};

function Confirm({ message, onClose }: ConfirmProps) {
  const variant = variants[message.variant || 'info'];
  const Icon = variant.icon;

  const handleConfirm = () => {
    if (message.onConfirm) {
      message.onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (message.onCancel) {
      message.onCancel();
    }
    onClose();
  };

  return (
    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
      <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
        <button
          type="button"
          className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          onClick={handleCancel}
        >
          <span className="sr-only">Fechar</span>
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div className="sm:flex sm:items-start">
        <div
          className={cn(
            'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
            message.variant === 'error'
              ? 'bg-red-100 dark:bg-red-900/30'
              : message.variant === 'warning'
              ? 'bg-yellow-100 dark:bg-yellow-900/30'
              : 'bg-blue-100 dark:bg-blue-900/30'
          )}
        >
          <Icon
            className={cn(
              'h-6 w-6',
              message.variant === 'error'
                ? 'text-red-600 dark:text-red-400'
                : message.variant === 'warning'
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-blue-600 dark:text-blue-400'
            )}
            aria-hidden="true"
          />
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
            {message.title}
          </h3>
          {message.content && (
            <div className="mt-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">{message.content}</p>
            </div>
          )}
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
        <Button variant={variant === 'error' ? 'danger' : 'primary'} onClick={handleConfirm}>
          {message.confirmText || 'Confirmar'}
        </Button>
        <Button variant="secondary" onClick={handleCancel}>
          {message.cancelText || 'Cancelar'}
        </Button>
      </div>
    </div>
  );
}

export default Confirm;
